import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { PlusCircle, Settings } from "lucide-react";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;
const hdrs = (token) => ({ Authorization: `Bearer ${token}` });

// ─────────────────────────────────────────────────────────────────────────────
// Create Scheme Modal
// ─────────────────────────────────────────────────────────────────────────────
function CreateSchemeModal({ open, onClose, token, onCreated }) {
  const [form, setForm] = useState({
    scheme_code: "", name: "", funding_source: "INSTITUTION",
    basis_type: "MERIT", coverage_type: "FIXED_AMOUNT", notes: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.scheme_code || !form.name) { toast.error("Code and name are required"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/finance/fm4a/schemes`, form, { headers: hdrs(token) });
      toast.success("Scholarship scheme created");
      onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="create-scheme-modal">
        <DialogHeader><DialogTitle>New Scholarship Scheme</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Scheme Code *</Label>
              <Input data-testid="scheme-code-input" value={form.scheme_code} onChange={e => setForm(f => ({ ...f, scheme_code: e.target.value }))} placeholder="e.g. MERIT-2026" />
            </div>
            <div>
              <Label>Name *</Label>
              <Input data-testid="scheme-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Display name" />
            </div>
          </div>
          <div>
            <Label>Funding Source</Label>
            <Select value={form.funding_source} onValueChange={v => setForm(f => ({ ...f, funding_source: v }))}>
              <SelectTrigger data-testid="scheme-funding-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="INSTITUTION">Institution</SelectItem>
                <SelectItem value="EXTERNAL">External</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Coverage Type</Label>
            <Select value={form.coverage_type} onValueChange={v => setForm(f => ({ ...f, coverage_type: v }))}>
              <SelectTrigger data-testid="scheme-coverage-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="FIXED_AMOUNT">Fixed Amount</SelectItem>
                <SelectItem value="PERCENTAGE">Percentage</SelectItem>
                <SelectItem value="FULL_FEE">Full Fee</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Basis Type</Label>
            <Input value={form.basis_type} onChange={e => setForm(f => ({ ...f, basis_type: e.target.value }))} placeholder="e.g. MERIT, NEED, SPORTS" />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="create-scheme-submit" onClick={submit} disabled={loading}>{loading ? "Creating…" : "Create Scheme"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Create Waiver Type Modal
// ─────────────────────────────────────────────────────────────────────────────
function CreateWaiverTypeModal({ open, onClose, token, onCreated }) {
  const [form, setForm] = useState({ type_code: "", name: "", default_reason_code: "WAIVER_APPLIED", notes: "" });
  const [loading, setLoading] = useState(false);

  const submit = async () => {
    if (!form.type_code || !form.name) { toast.error("Code and name are required"); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/finance/fm4a/waiver-types`, form, { headers: hdrs(token) });
      toast.success("Waiver type created");
      onCreated(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="create-waiver-type-modal">
        <DialogHeader><DialogTitle>New Waiver Type</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type Code *</Label>
              <Input data-testid="waiver-type-code-input" value={form.type_code} onChange={e => setForm(f => ({ ...f, type_code: e.target.value }))} placeholder="e.g. HARDSHIP" />
            </div>
            <div>
              <Label>Name *</Label>
              <Input data-testid="waiver-type-name-input" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="Display name" />
            </div>
          </div>
          <div>
            <Label>Default Reason Code</Label>
            <Select value={form.default_reason_code} onValueChange={v => setForm(f => ({ ...f, default_reason_code: v }))}>
              <SelectTrigger data-testid="waiver-type-reason-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="WAIVER_APPLIED">WAIVER_APPLIED</SelectItem>
                <SelectItem value="FEE_WAIVER">FEE_WAIVER</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="create-waiver-type-submit" onClick={submit} disabled={loading}>{loading ? "Creating…" : "Create Type"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main Admin Config Page
// ─────────────────────────────────────────────────────────────────────────────
export default function ScholarshipWaiverAdmin() {
  const [tab, setTab] = useState("schemes");
  const [schemes, setSchemes] = useState([]);
  const [waiverTypes, setWaiverTypes] = useState([]);
  const [showCreateScheme, setShowCreateScheme] = useState(false);
  const [showCreateWaiverType, setShowCreateWaiverType] = useState(false);
  const token = localStorage.getItem("token");

  const loadSchemes = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/finance/fm4a/schemes`, { headers: hdrs(token) });
      setSchemes(r.data);
    } catch { toast.error("Failed to load schemes"); }
  }, [token]);

  const loadWaiverTypes = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/finance/fm4a/waiver-types`, { headers: hdrs(token) });
      setWaiverTypes(r.data);
    } catch { toast.error("Failed to load waiver types"); }
  }, [token]);

  useEffect(() => { loadSchemes(); loadWaiverTypes(); }, [loadSchemes, loadWaiverTypes]);

  const deactivateScheme = async (id) => {
    try {
      await axios.patch(`${API}/api/finance/fm4a/schemes/${id}/deactivate`, {}, { headers: hdrs(token) });
      toast.success("Scheme deactivated"); loadSchemes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deactivateWaiverType = async (id) => {
    try {
      await axios.patch(`${API}/api/finance/fm4a/waiver-types/${id}/deactivate`, {}, { headers: hdrs(token) });
      toast.success("Waiver type deactivated"); loadWaiverTypes();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="scholarship-waiver-admin-page">
      <div className="flex items-center gap-2 mb-6">
        <Settings className="w-5 h-5 text-muted-foreground" />
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Scholarship & Waiver Configuration</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage scholarship schemes and waiver types. All start inactive until explicitly activated.</p>
        </div>
      </div>

      {/* NOT_CONFIGURED notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 text-sm text-amber-800">
        Production default: no schemes or waiver types are pre-configured. All must be created by an institution admin (INV-FM4-15).
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b mb-6">
        {[{ id: "schemes", label: "Scholarship Schemes" }, { id: "waiver-types", label: "Waiver Types" }].map(t => (
          <button
            key={t.id}
            data-testid={`admin-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "schemes" && (
        <div data-testid="schemes-admin-tab">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold">Scholarship Schemes ({schemes.length})</h3>
            <Button size="sm" data-testid="create-scheme-btn" onClick={() => setShowCreateScheme(true)}>
              <PlusCircle className="w-4 h-4 mr-1" /> New Scheme
            </Button>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="schemes-table">
              <thead>
                <tr className="bg-muted/50 border-b text-left text-xs text-muted-foreground">
                  <th className="py-3 px-4">Code</th>
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3 px-4">Funding</th>
                  <th className="py-3 px-4">Coverage</th>
                  <th className="py-3 px-4">Active</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {schemes.length === 0 && (
                  <tr><td colSpan={6} className="py-8 text-center text-muted-foreground text-xs">No schemes configured yet</td></tr>
                )}
                {schemes.map(s => (
                  <tr key={s.id} className="border-b hover:bg-muted/20" data-testid={`scheme-row-${s.id}`}>
                    <td className="py-3 px-4 font-mono text-xs">{s.scheme_code}</td>
                    <td className="py-3 px-4">{s.name}</td>
                    <td className="py-3 px-4 text-xs">{s.funding_source}</td>
                    <td className="py-3 px-4 text-xs">{s.coverage_type}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                        {s.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {s.active && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`deactivate-scheme-${s.id}`} onClick={() => deactivateScheme(s.id)}>
                          Deactivate
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "waiver-types" && (
        <div data-testid="waiver-types-admin-tab">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-base font-semibold">Waiver Types ({waiverTypes.length})</h3>
            <Button size="sm" data-testid="create-waiver-type-btn" onClick={() => setShowCreateWaiverType(true)}>
              <PlusCircle className="w-4 h-4 mr-1" /> New Waiver Type
            </Button>
          </div>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm" data-testid="waiver-types-table">
              <thead>
                <tr className="bg-muted/50 border-b text-left text-xs text-muted-foreground">
                  <th className="py-3 px-4">Code</th>
                  <th className="py-3 px-4">Name</th>
                  <th className="py-3 px-4">Reason Code</th>
                  <th className="py-3 px-4">Active</th>
                  <th className="py-3 px-4">Actions</th>
                </tr>
              </thead>
              <tbody>
                {waiverTypes.length === 0 && (
                  <tr><td colSpan={5} className="py-8 text-center text-muted-foreground text-xs">No waiver types configured yet</td></tr>
                )}
                {waiverTypes.map(wt => (
                  <tr key={wt.id} className="border-b hover:bg-muted/20" data-testid={`waiver-type-row-${wt.id}`}>
                    <td className="py-3 px-4 font-mono text-xs">{wt.type_code}</td>
                    <td className="py-3 px-4">{wt.name}</td>
                    <td className="py-3 px-4 font-mono text-xs">{wt.default_reason_code}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${wt.active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                        {wt.active ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="py-3 px-4">
                      {wt.active && (
                        <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`deactivate-waiver-type-${wt.id}`} onClick={() => deactivateWaiverType(wt.id)}>
                          Deactivate
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <CreateSchemeModal open={showCreateScheme} onClose={() => setShowCreateScheme(false)} token={token} onCreated={loadSchemes} />
      <CreateWaiverTypeModal open={showCreateWaiverType} onClose={() => setShowCreateWaiverType(false)} token={token} onCreated={loadWaiverTypes} />
    </div>
  );
}
